import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  RefreshCw,
  PlayCircle,
  Database,
  Calendar,
  FileSpreadsheet,
  FileText,
  FileJson,
  Download,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const FORMAT_META = {
  xlsx: { Icon: FileSpreadsheet, label: "Excel", tone: "emerald", color: "text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" },
  pdf: { Icon: FileText, label: "PDF", tone: "rose", color: "text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100" },
  json: { Icon: FileJson, label: "JSON", tone: "sky", color: "text-sky-600 bg-sky-50 border-sky-200 hover:bg-sky-100" },
};

export default function MonthlyResetPage() {
  const [status, setStatus] = useState(null);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Search + pagination
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // One period per page — current month on page 1, previous month on page 2,
  // older months progressively roll to the back pages (user request).
  const PAGE_SIZE = 1;

  const filteredArchives = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return archives;
    return archives.filter((a) => {
      // Allow matching "2026-06", "2026", "06", "Jun", "জুন", etc.
      const period = String(a.period || "").toLowerCase();
      if (period.includes(q)) return true;
      // Try parsing the period as YYYY-MM and matching month name
      const m = /^(\d{4})-(\d{2})$/.exec(period);
      if (m) {
        const monthNames = [
          "january", "february", "march", "april", "may", "june",
          "july", "august", "september", "october", "november", "december",
        ];
        const idx = parseInt(m[2], 10) - 1;
        if (idx >= 0 && idx < 12 && monthNames[idx].startsWith(q)) return true;
      }
      return false;
    });
  }, [archives, search]);

  const totalPages = Math.max(1, Math.ceil(filteredArchives.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedArchives = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredArchives.slice(start, start + PAGE_SIZE);
  }, [filteredArchives, safePage]);

  // Reset to page 1 whenever the search term changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: a }] = await Promise.all([
        api.get("/admin/monthly-reset/status"),
        api.get("/admin/monthly-reset/archives"),
      ]);
      setStatus(s);
      setArchives(a?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load reset info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/monthly-reset/run-now", {});
      toast.success(
        `Reset complete · ${data.total_deleted} rows wiped · archive ${data.archive?.total_rows || 0} rows saved`
      );
      setShowConfirm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setRunning(false);
    }
  };

  const downloadAgentZip = async (period) => {
    try {
      const res = await api.get(
        `/admin/monthly-reset/archive/${encodeURIComponent(period)}/agent-statements.zip`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-statements-${period}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Agent statements ZIP downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ZIP download failed");
    }
  };

  const downloadFile = async (period, filename, subdir) => {
    try {
      const path = `/admin/monthly-reset/archive/${encodeURIComponent(period)}/${encodeURIComponent(filename)}`;
      const res = await api.get(
        subdir ? `${path}?subdir=${encodeURIComponent(subdir)}` : path,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const last = status?.last_run;
  const lastErr = last?.errors && Object.keys(last.errors).length > 0;

  return (
    <div data-testid="admin-monthly-reset-page">
      <PageHeader
        title="Monthly Auto-Reset"
        subtitle="Automatic transaction cleanup with per-collection PDF archives saved before each wipe. Referral & Affiliate data is preserved."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
              data-testid="reset-refresh"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              className="h-9 px-3 inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white rounded-sm text-[13px] font-bold"
              data-testid="reset-run-now"
            >
              <PlayCircle size={13} /> Run reset now
            </button>
          </div>
        }
      />

      {/* Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <InfoCard
          tone="emerald"
          Icon={Calendar}
          label="Schedule"
          value={status?.schedule || "—"}
          sub={status?.scheduled ? "Active" : "Not running"}
        />
        <InfoCard
          tone="sky"
          Icon={Clock}
          label="Next auto run"
          value={status?.next_run_at ? new Date(status.next_run_at).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" }) : "—"}
          sub={status?.next_run_at ? `${status?.timezone || "Asia/Dhaka"} (Bangladesh time)` : "—"}
        />
        <InfoCard
          tone="violet"
          Icon={Database}
          label="Last run"
          value={last ? `${last.total_deleted} rows wiped` : "Never"}
          sub={last ? `${new Date(last.finished_at).toLocaleString()} · ${last.triggered_by}` : "Manual or auto on 1st of month"}
          warn={lastErr}
        />
      </div>

      {/* Last run details */}
      {last && (
        <div className="bg-white border border-gray-200 rounded-sm mb-4">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-[12px] uppercase tracking-wider text-gray-500 font-bold">Last reset details</div>
              <div className="text-[14px] font-semibold text-gray-900 mt-0.5">
                Period: <span className="font-mono">{last.period}</span> ·
                Deleted <span className="text-rose-600 font-bold">{last.total_deleted}</span> rows ·
                Archived <span className="text-emerald-600 font-bold">{last.archive?.total_rows || 0}</span> rows
              </div>
            </div>
            <span className="text-[11px] text-gray-500">{new Date(last.finished_at).toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 p-3">
            {Object.entries(last.deleted || {}).map(([col, n]) => (
              <div key={col} className="rounded-sm border border-gray-100 p-2 bg-gray-50">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold truncate" title={col}>{col}</div>
                <div className="text-[15px] font-bold tabular-nums text-gray-900">{n}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Protected Balances Notice */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4" data-testid="protected-collections-card">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-sm bg-emerald-100 text-emerald-700 grid place-items-center flex-shrink-0 font-bold">
            ✓
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-emerald-900">Balances are SAFE — never auto-reset</div>
            <div className="text-[12.5px] text-emerald-800 mt-1 leading-snug">
              User wallet balance, Agent wallet balance, Affiliate wallet balance and Promo Panel balance are stored in dedicated account collections (<code className="text-[11.5px] px-1 py-0.5 rounded bg-white/60 font-mono">users</code>, <code className="text-[11.5px] px-1 py-0.5 rounded bg-white/60 font-mono">agents</code>, <code className="text-[11.5px] px-1 py-0.5 rounded bg-white/60 font-mono">affiliate_accounts</code>) which are explicitly excluded from the monthly wipe. A defense-in-depth guard refuses to run the reset if any protected collection is accidentally added to the list.
            </div>
            {Array.isArray(status?.protected_collections) && status.protected_collections.length > 0 && (
              <details className="mt-2">
                <summary className="text-[11.5px] text-emerald-800 cursor-pointer hover:text-emerald-900 font-semibold">
                  View all {status.protected_collections.length} protected collections
                </summary>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {status.protected_collections.map((c) => (
                    <span key={c} className="text-[11px] px-2 py-0.5 rounded bg-white border border-emerald-200 font-mono text-emerald-900">
                      {c}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>

      {/* Archives */}
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-[12px] uppercase tracking-wider text-gray-500 font-bold">Available Archives</div>
              <div className="text-[13px] text-gray-500 mt-0.5">
                Each monthly reset generates a separate PDF for every transaction collection — saved before the wipe so you can lookup historical data anytime. Referral & Affiliate data is intentionally preserved (never auto-reset).
              </div>
            </div>
            <div className="relative sm:w-72 flex-shrink-0">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by month (e.g. 2026-06, June)"
                className="w-full pl-8 pr-3 py-2 text-[12.5px] border border-gray-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 font-mono"
                data-testid="archives-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-700"
                  data-testid="archives-search-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {archives.length > 0 && (
            <div className="mt-2 text-[11.5px] text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-700">
                {filteredArchives.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}
                –{Math.min(safePage * PAGE_SIZE, filteredArchives.length)}
              </span>{" "}
              of <span className="font-semibold text-gray-700">{filteredArchives.length}</span>
              {search && (
                <>
                  {" "}filtered from <span className="font-semibold text-gray-700">{archives.length}</span> total
                </>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading archives…</div>
        ) : archives.length === 0 ? (
          <div className="py-14 text-center" data-testid="no-archives">
            <div className="h-12 w-12 mx-auto rounded-md bg-gray-100 grid place-items-center mb-3">
              <Database className="text-gray-400" size={20} />
            </div>
            <div className="text-[14px] font-bold text-gray-700">No archives yet</div>
            <div className="text-[12.5px] text-gray-500 mt-1">
              Click <strong>Run reset now</strong> above to generate your first archive (or wait for the 1st of the month).
            </div>
          </div>
        ) : filteredArchives.length === 0 ? (
          <div className="py-14 text-center" data-testid="no-archives-filtered">
            <div className="h-12 w-12 mx-auto rounded-md bg-gray-100 grid place-items-center mb-3">
              <Search className="text-gray-400" size={20} />
            </div>
            <div className="text-[14px] font-bold text-gray-700">No matches for &quot;{search}&quot;</div>
            <div className="text-[12.5px] text-gray-500 mt-1">
              Try a year (<code className="font-mono">2026</code>), a month code (<code className="font-mono">06</code>),
              a full period (<code className="font-mono">2026-06</code>) or a month name (<code className="font-mono">June</code>).
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pagedArchives.map((arc) => (
              <div key={arc.period} className="p-4" data-testid={`archive-period-${arc.period}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Period</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Calendar size={14} className="text-emerald-600" />
                      <span className="text-[16px] font-bold text-gray-900 font-mono">{arc.period}</span>
                      <span className="text-[11.5px] text-gray-500 ml-2">{fmtBytes(arc.total_size)}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {arc.files.map((f) => {
                    const meta = FORMAT_META[f.format] || FORMAT_META.json;
                    const Icon = meta.Icon;
                    return (
                      <button
                        key={f.name}
                        onClick={() => downloadFile(arc.period, f.name)}
                        className={`group flex items-center justify-between gap-2 p-3 border rounded-sm transition ${meta.color}`}
                        data-testid={`download-${arc.period}-${f.format}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-9 w-9 rounded-sm grid place-items-center bg-white border border-current/20 flex-shrink-0">
                            <Icon size={17} />
                          </div>
                          <div className="min-w-0 text-left">
                            <div className="text-[12.5px] font-bold uppercase tracking-wider">{meta.label}</div>
                            <div className="text-[10.5px] opacity-70 truncate">{f.name}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[11px] font-mono">{fmtBytes(f.size)}</span>
                          <Download size={13} className="opacity-60 group-hover:opacity-100" />
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Per-Agent monthly statement PDFs — bundled as ZIP only */}
                {Array.isArray(arc.agent_statements) && arc.agent_statements.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-testid={`agent-statements-${arc.period}`}>
                    <button
                      type="button"
                      onClick={() => downloadAgentZip(arc.period)}
                      className="group w-full flex items-center justify-between gap-3 p-3 border border-indigo-200 bg-indigo-50/40 text-indigo-900 hover:bg-indigo-100 transition rounded-sm"
                      data-testid={`download-agent-zip-${arc.period}`}
                      title={`Download all ${arc.agent_statements.length} agent PDFs as one ZIP`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-9 w-9 rounded-sm grid place-items-center bg-white border border-indigo-200 flex-shrink-0">
                          <FileText size={17} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0 text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-[12.5px] font-bold uppercase tracking-wider">
                              Per-Agent Monthly Statements
                            </span>
                            <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold">
                              {arc.agent_statements.length} agent{arc.agent_statements.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="text-[10.5px] opacity-70 truncate">
                            agent-statements-{arc.period}.zip · contains {arc.agent_statements.length} PDF{arc.agent_statements.length === 1 ? "" : "s"} agents receive by email
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 px-2.5 py-1.5 rounded-sm bg-indigo-600 group-hover:bg-indigo-700 text-white text-[11.5px] font-bold uppercase tracking-wider transition">
                        <Download size={13} />
                        Download ZIP
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination footer */}
        {!loading && filteredArchives.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/60" data-testid="archives-pagination">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-bold uppercase tracking-wider border border-gray-200 rounded-sm bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              data-testid="archives-prev-page"
            >
              <ChevronLeft size={13} />
              Prev
            </button>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`min-w-[28px] h-7 px-2 text-[12px] font-bold rounded-sm border transition ${
                    n === safePage
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-indigo-50 hover:border-indigo-300"
                  }`}
                  data-testid={`archives-page-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-bold uppercase tracking-wider border border-gray-200 rounded-sm bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              data-testid="archives-next-page"
            >
              Next
              <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div
          onClick={() => !running && setShowConfirm(false)}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
          data-testid="run-reset-confirm-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md max-w-md w-full p-5 border border-gray-200 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-rose-100 text-rose-600 flex-shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-gray-900">Run monthly reset now?</h3>
                <p className="text-[12.5px] text-gray-500 mt-1">
                  This will <strong>archive</strong> each transactional collection to its own PDF, then <strong>delete all rows</strong> from {(status?.collections || []).length} transaction collections. Referral &amp; Affiliate data, account/config, and content collections are NOT touched.
                </p>
              </div>
            </div>
            <div className="rounded-sm bg-amber-50 border border-amber-200 text-[12px] text-amber-800 p-3 mb-4">
              <strong>Wipes:</strong> {(status?.collections || []).join(", ")}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={running}
                className="h-9 px-3 border border-gray-200 rounded-sm text-[13px] font-semibold hover:bg-gray-50 disabled:opacity-50"
                data-testid="run-reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={runNow}
                disabled={running}
                className="h-9 px-3 bg-rose-600 hover:bg-rose-700 text-white rounded-sm text-[13px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="run-reset-confirm"
              >
                {running ? <RefreshCw size={13} className="animate-spin" /> : <PlayCircle size={13} />}
                {running ? "Running…" : "Yes, archive & wipe"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ tone, Icon, label, value, sub, warn }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    sky: "border-sky-200 bg-sky-50/50",
    violet: "border-violet-200 bg-violet-50/50",
  };
  const iconTones = {
    emerald: "text-emerald-600 bg-emerald-100",
    sky: "text-sky-600 bg-sky-100",
    violet: "text-violet-600 bg-violet-100",
  };
  return (
    <div className={`rounded-sm p-4 border ${tones[tone]}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`h-7 w-7 rounded-sm grid place-items-center ${iconTones[tone]}`}>
          <Icon size={14} />
        </div>
        <div className="text-[10.5px] uppercase tracking-wider text-gray-600 font-semibold">{label}</div>
        {warn && <AlertTriangle size={13} className="text-amber-600 ml-auto" title="Last run had errors" />}
        {!warn && tone === "violet" && <CheckCircle2 size={13} className="text-emerald-600 ml-auto" />}
      </div>
      <div className="text-[16px] font-bold text-gray-900 break-words">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
